import { message } from 'antd';
import { TFunction } from 'i18next';

import { OpenClassCertificateEmailResult } from '../types/certificateEmail';

export type CertificateEmailOutcome = {
  sent: number;
  sentToContactPerson: number;
  alreadySent: number;
  noEmail: number;
  missingCertificate: number;
  failed: number;
};

export const certificateEmailOutcome = (
  results: OpenClassCertificateEmailResult[],
): CertificateEmailOutcome => ({
  sent: results.filter(({ status }) => status === 'sent').length,
  sentToContactPerson: results.filter(
    ({ status }) => status === 'sent_to_contact_person',
  ).length,
  alreadySent: results.filter(({ status }) => status === 'already_sent').length,
  noEmail: results.filter(({ status }) => status === 'no_email').length,
  missingCertificate: results.filter(
    ({ status }) => status === 'missing_certificate',
  ).length,
  failed: results.filter(({ status }) => status === 'failed').length,
});

export const reportCertificateEmailOutcome = (
  results: OpenClassCertificateEmailResult[],
  t: TFunction,
  requestFailed = false,
): void => {
  const {
    sent,
    sentToContactPerson,
    alreadySent,
    noEmail,
    missingCertificate,
    failed,
  } = certificateEmailOutcome(results);
  const delivered = [
    sent > 0
      ? t('views.OpenClasses.emailCertificatesSent', { count: sent })
      : null,
    sentToContactPerson > 0
      ? t('views.OpenClasses.emailCertificatesSentToContactPerson', {
          count: sentToContactPerson,
        })
      : null,
  ].filter(Boolean);

  if (
    !requestFailed &&
    alreadySent + noEmail + missingCertificate + failed === 0
  ) {
    message.success(delivered.join('. '));
    return;
  }

  // A batch where everyone was skipped went fine, nothing failed
  if (!requestFailed && alreadySent > 0 && alreadySent === results.length) {
    message.info(t('views.OpenClasses.emailCertificatesAllSkipped'));
    return;
  }

  const text = [
    ...delivered,
    alreadySent > 0
      ? t('views.OpenClasses.emailCertificatesSkipped', { count: alreadySent })
      : null,
    noEmail > 0
      ? t('views.OpenClasses.emailCertificatesNoEmail', { count: noEmail })
      : null,
    missingCertificate > 0
      ? t('views.OpenClasses.emailCertificatesMissingCertificate', {
          count: missingCertificate,
        })
      : null,
    failed > 0
      ? t('views.OpenClasses.emailCertificatesFailed', { count: failed })
      : null,
    requestFailed
      ? t('views.OpenClasses.emailCertificatesRequestFailed')
      : null,
  ]
    .filter(Boolean)
    .join('. ');

  if (sent + sentToContactPerson === 0) message.error(text);
  else message.warning(text);
};
